import { useCallback, useState } from 'react';

import { chatService } from '../services/chatService';
import { logger } from '../utils/logger';

const moduleLogger = logger.getModule('MessageFeedback');

interface MessageFeedbackState {
  isOpen: boolean;
  messageId: number | null;
  initialRating: number;
}

interface UseMessageFeedbackOptions {
  activeSessionId?: string | null;
  targetSessionId?: string | null;
}

const createInitialState = (): MessageFeedbackState => ({
  isOpen: false,
  messageId: null,
  initialRating: 1,
});

export const useMessageFeedback = ({
  activeSessionId,
  targetSessionId,
}: UseMessageFeedbackOptions) => {
  const [feedbackModal, setFeedbackModal] = useState<MessageFeedbackState>(createInitialState);

  const openFeedback = useCallback((messageId: number, rating: number) => {
    setFeedbackModal({
      isOpen: true,
      messageId,
      initialRating: rating,
    });
  }, []);

  const closeFeedback = useCallback(() => {
    setFeedbackModal(createInitialState());
  }, []);

  const submitFeedback = useCallback(async (rating: number, comments: string) => {
    if (!feedbackModal.messageId || !activeSessionId || !targetSessionId) {
      return;
    }

    const logContext = {
      messageId: feedbackModal.messageId,
      activeSessionId,
      targetSessionId,
    };

    try {
      await chatService.submitFeedback(
        feedbackModal.messageId,
        activeSessionId,
        targetSessionId,
        rating,
        comments || undefined,
      );
      moduleLogger.debug('Feedback submitted successfully', logContext);
    } catch (error) {
      moduleLogger.error('Failed to submit feedback', { ...logContext, error });
      throw error;
    }
  }, [activeSessionId, feedbackModal.messageId, targetSessionId]);

  return {
    feedbackModal,
    openFeedback,
    closeFeedback,
    submitFeedback,
  };
};